"use client";

import type { Toast as ToastType } from "react-hot-toast";
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from "lucide-react";
import { toast } from "react-hot-toast";
import { Button } from "@/components/ui/Button";
import cn from "@/lib/cn";

type ToastVariant = "success" | "error" | "warning" | "info";

const variantConfig: Record<
  ToastVariant,
  {
    icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
    iconColor: string;
    iconBg: string;
  }
> = {
  success: {
    icon: CheckCircle2,
    iconColor: "text-[#16A34A]",
    iconBg: "bg-[rgba(22,163,74,0.10)]",
  },
  error: {
    icon: XCircle,
    iconColor: "text-[#DC2626]",
    iconBg: "bg-[rgba(220,38,38,0.10)]",
  },
  warning: {
    icon: AlertTriangle,
    iconColor: "text-[#D97706]",
    iconBg: "bg-[rgba(217,119,6,0.10)]",
  },
  info: {
    icon: Info,
    iconColor: "text-[#1884F9]",
    iconBg: "bg-[rgba(24,132,249,0.10)]",
  },
};

function resolveVariant(t: ToastType): ToastVariant {
  if (t.type === "success") return "success";
  if (t.type === "error") return "error";
  if (t.variant === "warning" || t.variant === "info") return t.variant;
  return "info";
}

interface ToastProps {
  t: ToastType;
}

export function Toast({ t }: ToastProps) {
  const variant = resolveVariant(t);
  const { icon: Icon, iconColor, iconBg } = variantConfig[variant];

  const message = typeof t.message === "function" ? t.message(t) : t.message;

  const [title, description] = (() => {
    if (typeof message === "string" && message.includes("\n")) {
      const [first, ...rest] = message.split("\n");
      return [first, rest.join("\n")];
    }
    return [message, undefined];
  })();

  return (
    <div
      className={cn(
        "pointer-events-auto flex w-[340px] gap-3 rounded-[14px] bg-white p-3 transition-all duration-200",
        "shadow-[0_4px_20px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.05)]",
        description ? "items-start" : "items-center",
        t.visible ? "translate-y-0 opacity-100" : "translate-y-1 opacity-0",
      )}
    >
      {/* Icon badge */}
      <div
        className={cn(
          "flex size-8 shrink-0 items-center justify-center rounded-[8px]",
          iconBg,
        )}
      >
        <Icon className={cn("size-4", iconColor)} strokeWidth={2} />
      </div>

      {/* Text */}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-[13px] leading-snug font-semibold text-[#111827]">
          {title}
        </p>
        {description && (
          <p className="text-[12px] leading-snug font-medium text-[#6B7280]">
            {description}
          </p>
        )}
      </div>

      {/* Dismiss */}
      <Button
        icon={X}
        size="sm"
        onClick={() => toast.dismiss(t.id)}
        aria-label="Dismiss"
      />
    </div>
  );
}
